import { Text } from '@dsx-react/text';
import { useA11y } from '@dsx-react/provider';
import { useControllableState, classNames, Dialog, useThemeVariable } from '@dsx-react/util';
import { useFloating, autoUpdate, offset, flip, shift, arrow, useDismiss, useInteractions } from '@floating-ui/react';
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { UNSAFE_Icon } from '@dsx-react/icon';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Popover-module_dialog__w2gil {\n  margin: 0;\n  padding: 0;\n  border: none;\n  background: transparent;\n}\n.Popover-module_dialog__w2gil > .Popover-module_arrow__j4Opa::before {\n  border: var(--size-200) solid transparent;\n}\n.Popover-module_dialog__w2gil.Popover-module_side-top__-5RY6 > .Popover-module_arrow__j4Opa {\n  top: 100%;\n}\n.Popover-module_dialog__w2gil.Popover-module_side-top__-5RY6 > .Popover-module_arrow__j4Opa::before {\n  border-top-color: var(--background-color);\n}\n.Popover-module_dialog__w2gil.Popover-module_side-right__N8BLw > .Popover-module_arrow__j4Opa {\n  right: 100%;\n}\n.Popover-module_dialog__w2gil.Popover-module_side-right__N8BLw > .Popover-module_arrow__j4Opa::before {\n  border-right-color: var(--background-color);\n}\n.Popover-module_dialog__w2gil.Popover-module_side-bottom__rpkJT > .Popover-module_arrow__j4Opa {\n  bottom: 100%;\n}\n.Popover-module_dialog__w2gil.Popover-module_side-bottom__rpkJT > .Popover-module_arrow__j4Opa::before {\n  border-bottom-color: var(--background-color);\n}\n.Popover-module_dialog__w2gil.Popover-module_side-left__tCq7q > .Popover-module_arrow__j4Opa {\n  left: 100%;\n}\n.Popover-module_dialog__w2gil.Popover-module_side-left__tCq7q > .Popover-module_arrow__j4Opa::before {\n  border-left-color: var(--background-color);\n}\n\n.Popover-module_variant-tooltip__Y9-5g {\n  --background-color: rgba(var(--color-gray-900-rgb), 0.75);\n}\n\n.Popover-module_variant-popover__3ss1Z,\n.Popover-module_variant-dialog__GgZcq {\n  --background-color: var(--color-gray-0);\n}\n\n.Popover-module_arrow__j4Opa::before {\n  content: \"\";\n  display: block;\n}\n\n.Popover-module_contents__YWiRG {\n  position: relative;\n  border-radius: var(--border-radius-default);\n  background-color: var(--background-color);\n}\n\n.Popover-module_variant-tooltip__Y9-5g .Popover-module_contents__YWiRG {\n  padding: var(--size-200);\n}\n\n.Popover-module_variant-popover__3ss1Z .Popover-module_contents__YWiRG {\n  padding: var(--size-400);\n  border-radius: var(--border-radius-default);\n  box-shadow: var(--UNSAFE-dsx-popover-box-shadow);\n}\n\n.Popover-module_variant-dialog__GgZcq .Popover-module_contents__YWiRG {\n  padding: var(--size-500);\n  border-radius: var(--border-radius-default);\n  box-shadow: var(--UNSAFE-dsx-popover-box-shadow);\n  min-width: 480px;\n  max-width: min(90vw, 80ch);\n}\n\n.Popover-module_tooltip-icon__Jk-0- {\n  vertical-align: -0.125em;\n}\n\n.Popover-module_tooltip-text__IbO-P {\n  white-space: pre-line;\n}\n\n.Popover-module_dialog-footer__c4vft {\n  display: flex;\n  justify-content: flex-end;\n  gap: var(--size-300);\n}\n\n.Popover-module_dialog-contents__toW-s {\n  display: flex;\n  flex-direction: column;\n  gap: var(--size-600);\n}\n\n:where(.dsx-theme-legacy) .Popover-module_dialog-footer__c4vft {\n  padding-top: var(--size-400);\n  border-top: 1px solid var(--color-gray-100);\n}";
var styles = {"dialog":"Popover-module_dialog__w2gil","arrow":"Popover-module_arrow__j4Opa","side-top":"Popover-module_side-top__-5RY6","side-right":"Popover-module_side-right__N8BLw","side-bottom":"Popover-module_side-bottom__rpkJT","side-left":"Popover-module_side-left__tCq7q","variant-tooltip":"Popover-module_variant-tooltip__Y9-5g","variant-popover":"Popover-module_variant-popover__3ss1Z","variant-dialog":"Popover-module_variant-dialog__GgZcq","contents":"Popover-module_contents__YWiRG","tooltip-icon":"Popover-module_tooltip-icon__Jk-0-","tooltip-text":"Popover-module_tooltip-text__IbO-P","dialog-footer":"Popover-module_dialog-footer__c4vft","dialog-contents":"Popover-module_dialog-contents__toW-s"};
styleInject(css_248z);

function PopoverBase(props) {
  var _middlewareData$arrow, _middlewareData$arrow2;
  const {
    isOpen: controlledOpen,
    defaultOpen = false,
    onOpenChange,
    placement: idealPlacement = 'bottom',
    showTip = false,
    contents,
    variant,
    children,
    className: callerClassName,
    tag
  } = props;
  const [open, setOpen] = useControllableState(controlledOpen, defaultOpen, onOpenChange);
  const {
    portalTarget
  } = useA11y();
  const arrowRef = useRef(null);
  const {
    floatingStyles,
    refs,
    placement,
    middlewareData,
    context
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: variant === 'dialog' ? `${idealPlacement}-start` : idealPlacement,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({
      crossAxis: false
    }), shift(), showTip && arrow({
      element: arrowRef
    })]
  });
  const dismiss = useDismiss(context);
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([dismiss]);
  const className = classNames('dsx-component', styles.dialog, styles[`side-${placement.split('-')[0]}`], styles[`variant-${variant}`], callerClassName);
  function handleAnchorClick() {
    setOpen(!open);
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("span", {
      style: {
        display: 'inline-block'
      },
      ref: refs.setReference,
      "data-dsx--popover--anchor": tag,
      ...getReferenceProps({
        onClick: handleAnchorClick
      }),
      children: children
    }), portalTarget && /*#__PURE__*/createPortal( /*#__PURE__*/jsxs(Dialog, {
      focusOnOpen: false,
      isOpen: open,
      className: className,
      ref: refs.setFloating,
      style: floatingStyles,
      "data-dsx--popover": tag,
      children: [/*#__PURE__*/jsx("div", {
        className: styles.contents,
        ...getFloatingProps(),
        children: contents
      }), showTip && /*#__PURE__*/jsx("div", {
        className: styles.arrow,
        ref: arrowRef,
        style: {
          position: 'absolute',
          left: (_middlewareData$arrow = middlewareData.arrow) === null || _middlewareData$arrow === void 0 ? void 0 : _middlewareData$arrow.x,
          top: (_middlewareData$arrow2 = middlewareData.arrow) === null || _middlewareData$arrow2 === void 0 ? void 0 : _middlewareData$arrow2.y
        }
      })]
    }), portalTarget)]
  });
}

function Popover(_ref) {
  let {
    contents,
    ...props
  } = _ref;
  return /*#__PURE__*/jsx(PopoverBase, {
    ...props,
    contents: /*#__PURE__*/jsx(Text, {
      variant: "body-small",
      children: contents
    }),
    variant: "popover"
  });
}

function PopoverDialogContents(_ref) {
  let {
    header,
    footer,
    contents
  } = _ref;
  const ref = useRef(null);
  const headerVariant = useThemeVariable('UNSAFE-dsx-modal-header-variant', ref);
  const headerEl = typeof header === 'string' ? /*#__PURE__*/jsx(Text, {
    variant: headerVariant,
    children: header
  }) : header;
  const footerEl = footer != null && /*#__PURE__*/jsx("div", {
    className: styles['dialog-footer'],
    children: footer
  });
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    className: styles['dialog-contents'],
    children: [headerEl, /*#__PURE__*/jsx("div", {
      children: contents
    }), footerEl]
  });
}

function PopoverDialog(_ref) {
  let {
    contents,
    header,
    footer,
    ...props
  } = _ref;
  return /*#__PURE__*/jsx(PopoverBase, {
    ...props,
    variant: "dialog",
    contents:
    /*#__PURE__*/
    /* Needs to be a separate component so useThemeVariable hooks run after modal mount */
    jsx(PopoverDialogContents, {
      contents: contents,
      header: header,
      footer: footer
    })
  });
}

function Tooltip(_ref) {
  let {
    contents,
    icon,
    ...props
  } = _ref;
  return /*#__PURE__*/jsx(PopoverBase, {
    ...props,
    contents: /*#__PURE__*/jsxs(Text, {
      color: "color-neutral-50-static",
      variant: "body-compact-small",
      className: styles['tooltip-text'],
      children: [icon && /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(UNSAFE_Icon, {
          className: styles['tooltip-icon'],
          icon: icon
        }), ' ']
      }), contents]
    }),
    variant: "tooltip"
  });
}

export { Popover, PopoverDialog, Tooltip };
